import React from "react";
import { AppProvider, Frame, Navigation, TopBar } from "@shopify/polaris";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import DashboardPage from "./pages/DashboardPage";
import GamesCatalogPage from "./pages/GamesCatalogPage";
import WheelTemplateEditPage from "./pages/WheelTemplateEditPage";
import ScratchGameConfigPage from "./pages/ScratchGameConfigPage";
import CampaignsListPage from "./pages/CampaignsListPage";
import CampaignFormPage from "./pages/CampaignFormPage";
import RewardsPage from "./pages/RewardsPage";
import GamePlaygroundPage from "./pages/GamePlaygroundPage";
import GamePreviewPage from "./pages/GamePreviewPage";
import SegmentsPage from "./pages/SegmentsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import CampaignAnalyticsPage from "./pages/CampaignAnalyticsPage";
import SettingsPage from "./pages/SettingsPage";
import FraudDetectionPage from "./pages/FraudDetectionPage";
import { initializeAppBridge, isEmbedded } from "./utils/app-bridge";
import { APP_NAV_ITEMS, findActiveNavItemIndex } from "./config/app-navigation";
import {
  ensureEmbeddedNavigationMenu,
  syncEmbeddedNavigationActive,
} from "./utils/embedded-navigation";

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavigationActive, setMobileNavigationActive] = React.useState(false);
  const embedded = isEmbedded();

  React.useEffect(() => {
    if (embedded) {
      try {
        const app = initializeAppBridge();
        ensureEmbeddedNavigationMenu(app);
      } catch (error) {
        console.error("[App Bridge] Initialization failed:", error);
      }
    } else {
      console.warn("[App Bridge] Not running in embedded context (development mode)");
    }
  }, [embedded]);

  React.useEffect(() => {
    if (!embedded) {
      return;
    }
    syncEmbeddedNavigationActive(location.pathname);
  }, [embedded, location.pathname]);

  const toggleMobileNavigationActive = () => {
    setMobileNavigationActive((prev) => !prev);
  };

  const topBarMarkup = !embedded ? (
    <TopBar showNavigationToggle onNavigationToggle={toggleMobileNavigationActive} />
  ) : undefined;

  const activeNavIdx = findActiveNavItemIndex(location.pathname);

  const navigationMarkup = (
    <Navigation location={location.pathname}>
      <Navigation.Section
        items={APP_NAV_ITEMS.map((item, i) => ({
          label: item.label,
          icon: item.icon,
          selected: i === activeNavIdx,
          onClick: () => navigate(item.path),
        }))}
      />
    </Navigation>
  );

  return (
    <AppProvider i18n={{}}>
      <Frame
        topBar={topBarMarkup}
        navigation={embedded ? undefined : navigationMarkup}
        showMobileNavigation={!embedded && mobileNavigationActive}
        onNavigationDismiss={toggleMobileNavigationActive}
      >
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/games" element={<GamesCatalogPage />} />
          <Route path="/games/wheel-templates/:templateId" element={<WheelTemplateEditPage />} />
          <Route path="/games/scratch-config" element={<ScratchGameConfigPage />} />
          <Route path="/campaigns" element={<CampaignsListPage />} />
          <Route path="/campaigns/:id" element={<CampaignFormPage />} />
          <Route path="/campaigns/:id/analytics" element={<CampaignAnalyticsPage />} />
          <Route path="/rewards" element={<RewardsPage />} />
          <Route path="/segments" element={<SegmentsPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/fraud" element={<FraudDetectionPage />} />
          <Route path="/playground" element={<GamePlaygroundPage />} />
          <Route path="/games/preview" element={<GamePreviewPage />} />
        </Routes>
      </Frame>
    </AppProvider>
  );
}

export default App;
